import {
  effectAsParsed,
  effectResistDefaults,
  effectRulesDefaults,
  zoneDefaultsDefaults,
} from '../../../lib/effects/defaults';
import {
  EFFECT_APPLIES_TO,
  EFFECT_DURATION_KINDS,
  EFFECT_POLARITIES,
  EFFECT_SCOPES,
  EFFECT_STACK_MODES,
  EFFECT_TRIGGERS,
  GROUP_SCOPES,
  MODIFIER_REGISTRY,
  findModifierKey,
  isStatKey,
  triggerListParams,
  triggerParams,
  type EffectRulesDef,
  type EffectTriggerDef,
  type ModifierClampDef,
  type ModifierDef,
  type StatusEffectDef,
  type ZoneDef,
} from '../../../lib/effects/types';
import { STAT_IDS, statLabel } from '../../../lib/hb/types';
import { ACTIVITY_CATEGORIES } from '../../../lib/skills/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { issuesUnder, type FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
  inputCls,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';
import { ZoneMap } from './ZoneMap';

const opts = (list: readonly string[]) => list.map((v) => ({ value: v, label: v }));
const KEY_OPTIONS = MODIFIER_REGISTRY.map((k) => ({
  value: k.key,
  label: `${k.key} (${k.op}; ${k.scopes.join(', ')})`,
}));

/** A comma-separated list of numbers, applied when it parses (stack values, resist thresholds). */
function NumberListField({
  label,
  hint,
  value,
  integer,
  issues,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number[] | null | undefined;
  integer?: boolean;
  issues?: FieldErrors[string];
  onChange: (next: number[]) => void;
}) {
  return (
    <Field label={label} hint={hint} issues={issues}>
      {(id, invalid) => (
        <input
          id={id}
          aria-invalid={invalid}
          className={`${inputCls} font-mono`}
          defaultValue={(value ?? []).join(', ')}
          onBlur={(e) => {
            const nums = e.target.value
              .split(',')
              .map((x) => x.trim())
              .filter((x) => x !== '')
              .map(Number);
            if (nums.every((n) => Number.isFinite(n) && (!integer || Number.isInteger(n))))
              onChange(nums);
          }}
        />
      )}
    </Field>
  );
}

/**
 * One effect (`statusEffects[]`, shared-cs `StatusEffectDef`, ADR 0045): who it touches, the modifiers (keys from `ModifierRegistry`;
 * the op follows the key), the duration, stacking, a debuff's resist, the server events that apply it, and the player-facing text.
 */
export function StatusEffectForm({ value, onChange, errors }: FormProps<StatusEffectDef>) {
  const e = effectAsParsed(value ?? {});
  const set = (patch: Partial<StatusEffectDef>) => onChange({ ...value, ...patch });
  const group = (GROUP_SCOPES as readonly string[]).includes(e.scope ?? '');
  const kind = e.duration?.kind ?? 'timed';
  const s = e.stacking ?? {};
  const modifiers = (e.modifiers ?? []).map((m) => m ?? { key: '', op: 'add', value: 0 });
  const triggers = (e.triggers ?? []).map((t) => t ?? { event: '' });
  return (
    <>
      <Fieldset legend="Effect">
        <TextField
          label="Id"
          mono
          value={e.id}
          issues={errors.id}
          onChange={(v) => set({ id: v })}
        />
        <TextField
          label="Name"
          value={e.name}
          issues={errors.name}
          onChange={(v) => set({ name: v })}
        />
        <TextField
          label="Family"
          mono
          hint="Effects of one family replace each other (the stronger stays)."
          value={e.family}
          issues={errors.family}
          onChange={(v) => set({ family: v })}
        />
        <SelectField
          label="Polarity"
          value={e.polarity}
          options={opts(EFFECT_POLARITIES)}
          issues={errors.polarity}
          onChange={(v) => set({ polarity: v ?? 'buff' })}
        />
        <SelectField
          label="Scope"
          value={e.scope}
          options={opts(EFFECT_SCOPES)}
          hint="player, one survivor, a team, the whole crew, or the base."
          issues={errors.scope}
          onChange={(v) =>
            set({
              scope: v ?? 'player',
              ...((GROUP_SCOPES as readonly string[]).includes(v ?? '')
                ? {}
                : { appliesTo: 'everyone' }),
            })
          }
        />
        {group && (
          <SelectField
            label="Applies to"
            value={e.appliesTo}
            options={opts(EFFECT_APPLIES_TO)}
            issues={errors.appliesTo}
            onChange={(v) => set({ appliesTo: v ?? 'everyone' })}
          />
        )}
        <SelectField
          label="Mirror"
          value={e.mirror ?? ''}
          options={[{ value: 'copies', label: 'copies' }]}
          none="none"
          hint="copies: also applied to every living QR copy of this player in other players' games, same expiry (player scope only)."
          issues={errors.mirror}
          onChange={(v) => set({ mirror: v ?? '' })}
        />
        <CheckboxField
          label="Hidden"
          hint="Not shown to players (tape, file)."
          checked={!!e.hidden}
          onChange={(on) => set({ hidden: on })}
        />
      </Fieldset>

      <Fieldset legend="Modifiers">
        <ListEditor<ModifierDef>
          label="Modifiers"
          what="modifier"
          hint="add: points or flat amounts (stat.* are stat points); mult: a factor (0.9 = 10% less)."
          items={modifiers}
          issues={errors.modifiers}
          onChange={(next) => set({ modifiers: next })}
          make={() => ({ key: 'stat.nrv', op: 'add', value: 1 })}
          row={(m, j, setM) => {
            const key = findModifierKey(m.key);
            return (
              <div className="grid gap-1 sm:grid-cols-[2fr_1fr]">
                <SelectInput
                  label={`Modifier ${j + 1} key`}
                  value={m.key}
                  options={KEY_OPTIONS}
                  invalid={!!issuesUnder(errors, `modifiers.${j}`).length}
                  onChange={(k) => setM({ ...m, key: k ?? '', op: findModifierKey(k)?.op ?? m.op })}
                />
                <NumberInput
                  label={`Modifier ${j + 1} value`}
                  step={0.05}
                  value={m.value}
                  onChange={(v) => setM({ ...m, value: v ?? 0 })}
                />
                <p className="text-xs text-bruise sm:col-span-2">
                  {key
                    ? `${key.op}, neutral ${key.neutral}; read by ${key.consumer}${key.rideOnTicket ? '; rides on the raid ticket' : ''}.`
                    : 'Pick a registered key.'}
                  {m.op && key && m.op !== key.op ? ` (op is '${m.op}')` : ''}
                </p>
                <div className="sm:col-span-2">
                  <IssueList issues={issuesUnder(errors, `modifiers.${j}`)} />
                </div>
              </div>
            );
          }}
        />
      </Fieldset>

      <Fieldset legend="Duration and stacking">
        <SelectField
          label="Duration kind"
          value={kind}
          options={opts(EFFECT_DURATION_KINDS)}
          hint="timed: hours; zone: while present; until_cleared: until ended; derived: computed (home skills)."
          issues={issuesUnder(errors, 'duration')}
          onChange={(v) => set({ duration: { ...e.duration, kind: v ?? 'timed' } })}
        />
        {kind === 'timed' && (
          <NumberField
            label="Hours"
            min={0}
            value={e.duration?.hours}
            onChange={(v) => set({ duration: { ...e.duration, hours: v ?? 0 } })}
          />
        )}
        <SelectField
          label="On repeat"
          value={s.onRepeat}
          options={opts(EFFECT_STACK_MODES)}
          hint="refresh: restart the clock; extend: add hours; stack: count up; ignore: keep the first."
          issues={errors['stacking.onRepeat']}
          onChange={(v) => set({ stacking: { ...s, onRepeat: v ?? 'refresh' } })}
        />
        {s.onRepeat === 'extend' && (
          <>
            <NumberField
              label="Add hours"
              min={0}
              value={s.addH}
              issues={errors['stacking.addH']}
              onChange={(v) => set({ stacking: { ...s, addH: v ?? 0 } })}
            />
            <NumberField
              label="Max hours"
              min={0}
              value={s.maxH}
              issues={errors['stacking.maxH']}
              onChange={(v) => set({ stacking: { ...s, maxH: v ?? 0 } })}
            />
          </>
        )}
        {s.onRepeat === 'stack' && (
          <>
            <NumberField
              label="Max stacks"
              integer
              min={0}
              value={s.max}
              issues={errors['stacking.max']}
              onChange={(v) => set({ stacking: { ...s, max: v ?? 0 } })}
            />
            <NumberListField
              label="Scale per stack"
              hint="One value per stack: 1, 2 = the second stack doubles every modifier."
              value={s.values}
              issues={errors['stacking.values']}
              onChange={(values) => set({ stacking: { ...s, values } })}
            />
          </>
        )}
      </Fieldset>

      <Fieldset legend="Resist (debuffs)">
        <div className="sm:col-span-2">
          <CheckboxField
            label="Resisted by a stat"
            hint="The target's base stat ignores whole points of stat drops and shortens the duration."
            checked={e.resist != null}
            onChange={(on) => set({ resist: on ? effectResistDefaults() : null })}
          />
          <IssueList issues={errors.resist} />
        </div>
        {e.resist && (
          <>
            <SelectField
              label="Stat"
              value={e.resist.stat}
              options={STAT_IDS.map((x) => ({ value: x, label: statLabel(x) }))}
              issues={errors['resist.stat']}
              onChange={(v) => set({ resist: { ...e.resist, stat: v ?? 'nrv' } })}
            />
            <NumberListField
              label="Ignore a point at"
              hint="Ascending stat values: 6, 9 = ignore 1 point at 6 and 2 at 9."
              integer
              value={e.resist.ignoreAt}
              issues={errors['resist.ignoreAt']}
              onChange={(ignoreAt) => set({ resist: { ...e.resist, ignoreAt } })}
            />
            <NumberField
              label="Shorten per point"
              min={0}
              max={1}
              step={0.01}
              value={e.resist.shortenPerPt}
              issues={errors['resist.shortenPerPt']}
              onChange={(v) => set({ resist: { ...e.resist, shortenPerPt: v ?? 0 } })}
            />
            <NumberField
              label="Shorten above"
              integer
              min={0}
              max={10}
              value={e.resist.shortenAbove}
              issues={errors['resist.shortenAbove']}
              onChange={(v) => set({ resist: { ...e.resist, shortenAbove: v ?? 0 } })}
            />
            <NumberField
              label="Shortest duration multiplier"
              min={0}
              max={1}
              step={0.05}
              value={e.resist.minDurationMult}
              issues={errors['resist.minDurationMult']}
              onChange={(v) => set({ resist: { ...e.resist, minDurationMult: v ?? 0 } })}
            />
          </>
        )}
      </Fieldset>

      <Fieldset legend="Triggers">
        <ListEditor<EffectTriggerDef>
          label="Triggers"
          what="trigger"
          hint="Server events that apply this effect (never for zone or derived effects)."
          items={triggers}
          issues={errors.triggers}
          onChange={(next) => set({ triggers: next })}
          make={() => ({ event: 'beat', params: {} })}
          row={(t, j, setT) => (
            <div className="grid gap-1 sm:grid-cols-2">
              <SelectInput
                label={`Trigger ${j + 1} event`}
                value={t.event}
                options={opts(EFFECT_TRIGGERS)}
                onChange={(v) => setT({ event: v ?? '', params: {} })}
              />
              {triggerListParams(t.event).map((p) => (
                <CategoryChecks
                  key={p}
                  label={`Trigger ${j + 1} ${p}`}
                  value={t.listParams?.[p]}
                  onChange={(list) => {
                    const listParams = { ...t.listParams };
                    if (list.length === 0) delete listParams[p];
                    else listParams[p] = list;
                    setT({ ...t, listParams });
                  }}
                />
              ))}
              {triggerParams(t.event).map((p) => (
                <NumberInput
                  key={p}
                  label={`Trigger ${j + 1} ${p}`}
                  placeholder={p}
                  nullable
                  min={0}
                  value={t.params?.[p] ?? undefined}
                  onChange={(v) => {
                    const params = { ...t.params };
                    if (v == null) delete params[p];
                    else params[p] = v;
                    setT({ ...t, params });
                  }}
                />
              ))}
              <div className="sm:col-span-2">
                <IssueList issues={issuesUnder(errors, `triggers.${j}`)} />
              </div>
            </div>
          )}
        />
      </Fieldset>

      <Fieldset legend="Text">
        <TextField
          label="Tape"
          hint="The short label on the survivor's tape (GRIEF)."
          value={e.text?.tape}
          onChange={(v) => set({ text: { ...e.text, tape: v } })}
        />
        <TextField
          label="Icon"
          mono
          value={e.text?.icon}
          onChange={(v) => set({ text: { ...e.text, icon: v } })}
        />
        <TextField
          label="Cause text"
          value={e.text?.causeText}
          onChange={(v) => set({ text: { ...e.text, causeText: v } })}
        />
        <TextField
          label="End text"
          value={e.text?.endText}
          onChange={(v) => set({ text: { ...e.text, endText: v } })}
        />
        <StringListEditor
          label="Stack names"
          hint="One per stack (Hungry, Starving)."
          value={e.text?.stackNames ?? []}
          errors={errors}
          base="text.stackNames"
          onChange={(next) => set({ text: { ...e.text, stackNames: next } })}
        />
      </Fieldset>

      {e.derived && (
        <Fieldset legend="Derived home skill">
          <p className="text-xs text-bruise sm:col-span-2">
            Computed from the best stat at home (card 5); the perk's number is the perk's own knob.
          </p>
          <SelectField
            label="Skill"
            value={e.derived.skill}
            options={opts(['build', 'beds'])}
            issues={errors['derived.skill']}
            onChange={(v) => set({ derived: { ...e.derived, skill: v ?? '' } })}
          />
          <SelectField
            label="Stat"
            value={e.derived.stat}
            options={STAT_IDS.map((x) => ({ value: x, label: statLabel(x) }))}
            issues={errors['derived.stat']}
            onChange={(v) => set({ derived: { ...e.derived, stat: v ?? '' } })}
          />
          <NumberField
            label="Per point"
            step={0.01}
            value={e.derived.perPoint}
            issues={errors['derived.perPoint']}
            onChange={(v) => set({ derived: { ...e.derived, perPoint: v ?? 0 } })}
          />
          <TextField
            label="Perk id"
            mono
            value={e.derived.perkId}
            issues={errors['derived.perkId']}
            onChange={(v) => set({ derived: { ...e.derived, perkId: v } })}
          />
        </Fieldset>
      )}
    </>
  );
}

/** Checkboxes over the draft's effects of duration kind `zone` (plus any listed id that isn't one, so it can be unticked). */
function ZoneEffectPicker({
  label,
  value,
  onChange,
  issues,
}: {
  label: string;
  value: (string | null)[] | null | undefined;
  onChange: (next: string[]) => void;
  issues?: FieldErrors[string];
}) {
  const b = useContentBundle();
  const zoneEffects = (b.statusEffects ?? [])
    .filter((x) => x != null && effectAsParsed(x).duration?.kind === 'zone')
    .map((x) => x!.id ?? '');
  const list = (value ?? []).map((x) => x ?? '');
  const choices = [...new Set([...zoneEffects, ...list])];
  return (
    <div className="sm:col-span-2">
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      {choices.length === 0 && (
        <p className="text-xs text-bruise">No zone effects in the draft yet.</p>
      )}
      <div className="mt-1 flex flex-wrap gap-3">
        {choices.map((id) => (
          <label key={id} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={list.includes(id)}
              onChange={(ev) =>
                onChange(ev.target.checked ? [...list, id] : list.filter((x) => x !== id))
              }
            />
            <span className={zoneEffects.includes(id) ? 'font-mono' : 'font-mono text-blam'}>
              {id}
            </span>
          </label>
        ))}
      </div>
      <IssueList issues={issues} />
    </div>
  );
}

const CLAMPS = ['sumMin', 'sumMax', 'finalMin', 'finalMax'] as const;

/**
 * `effectRules` (shared-cs `EffectRulesDef`): how many effects one target holds, the ± cap on stat effects, per-key clamps (blank =
 * unbounded; stat keys take no sum clamp), the zone defaults and the base zones' effects. Absent fields show the C# defaults.
 */
export function EffectRulesForm({ value, onChange, errors }: FormProps<EffectRulesDef>) {
  const r = withDefaults(effectRulesDefaults(), value ?? {});
  const zd = { ...zoneDefaultsDefaults(), ...r.zoneDefaults };
  const bz = r.baseZoneEffects ?? {};
  const keys = r.keys ?? {};
  const set = (patch: Partial<EffectRulesDef>) => onChange({ ...r, ...patch });
  const setClamp = (key: string, field: (typeof CLAMPS)[number], v: number | null) => {
    const c: ModifierClampDef = { ...keys[key], [field]: v };
    const next = { ...keys };
    if (CLAMPS.every((f) => c[f] == null)) delete next[key];
    else next[key] = { sumMin: null, sumMax: null, finalMin: null, finalMax: null, ...c };
    set({ keys: next });
  };
  const zdNum = (k: keyof typeof zd, label: string, hint: string, nullable = false) => (
    <NumberField
      label={label}
      hint={hint}
      nullable={nullable}
      integer={k.endsWith('Minutes')}
      min={0}
      value={zd[k]}
      issues={errors[`zoneDefaults.${k}`]}
      onChange={(v) => set({ zoneDefaults: { ...zd, [k]: nullable ? v : (v ?? 0) } })}
    />
  );
  const unknownKeys = Object.keys(keys).filter((k) => !findModifierKey(k));
  return (
    <>
      <Fieldset legend="Limits">
        <NumberField
          label="Max active"
          hint="Effects one target holds at once."
          integer
          min={1}
          value={r.maxActive}
          issues={errors.maxActive}
          onChange={(v) => set({ maxActive: v ?? 0 })}
        />
        <NumberField
          label="Stat cap"
          hint="Effects move a stat at most this many points either way (conditions sit outside)."
          integer
          min={0}
          value={r.statCap}
          issues={errors.statCap}
          onChange={(v) => set({ statCap: v ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Per-key clamps">
        <div className="overflow-x-auto sm:col-span-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase">
                <th className="pr-2">Key</th>
                {CLAMPS.map((f) => (
                  <th key={f} className="pr-2">
                    {f}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODIFIER_REGISTRY.map((k) => (
                <tr key={k.key} className="align-top">
                  <td className="pr-2 font-mono text-xs">
                    {k.key} <span className="text-bruise">({k.op})</span>
                    <IssueList issues={errors[`keys.${k.key}`]} />
                  </td>
                  {CLAMPS.map((f) => (
                    <td key={f} className="pr-2">
                      {isStatKey(k.key) && f.startsWith('sum') ? (
                        <span className="text-xs text-bruise">statCap</span>
                      ) : (
                        <NumberInput
                          label={`${k.key} ${f}`}
                          nullable
                          step={0.05}
                          value={keys[k.key]?.[f]}
                          onChange={(v) => setClamp(k.key, f, v)}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {unknownKeys.map((k) => (
            <IssueList key={k} issues={errors[`keys.${k}`]} />
          ))}
          <p className="mt-1 text-xs text-bruise">
            sum bounds the effects' combined contribution (a sum for add keys, a product for mult
            keys); final bounds the value after the base. Blank = no bound.
          </p>
        </div>
      </Fieldset>
      <Fieldset legend="Zone defaults">
        {zdNum('radiusM', 'Radius m', 'A content zone with radius 0 uses this (10..5000).')}
        {zdNum('homeRadiusM', 'Home radius m', 'Blank = the check-in radius.', true)}
        {zdNum('campRadiusM', 'Outpost radius m', 'Blank = the check-in radius.', true)}
        {zdNum(
          'staleMinutes',
          'Stale minutes',
          'Presence lapses this long after the last confirming ping.',
        )}
        {zdNum('homeStaleMinutes', 'Home stale minutes', 'For the home zone.')}
        {zdNum('campStaleMinutes', 'Outpost stale minutes', 'For outpost zones.')}
        <IssueList issues={errors.zoneDefaults} />
      </Fieldset>
      <Fieldset legend="Base zone effects">
        <ZoneEffectPicker
          label="At home"
          value={bz.home}
          issues={errors['baseZoneEffects.home']}
          onChange={(home) => set({ baseZoneEffects: { ...bz, home } })}
        />
        <ZoneEffectPicker
          label="At an outpost"
          value={bz.camp}
          issues={errors['baseZoneEffects.camp']}
          onChange={(camp) => set({ baseZoneEffects: { ...bz, camp } })}
        />
        <IssueList issues={errors.baseZoneEffects} />
      </Fieldset>
    </>
  );
}

/**
 * A designer zone (`zones[]`, shared-cs `ZoneDef`, ADR 0045 §4): a place on the map that grants zone effects while the server believes
 * the player is there. Draw it on the Leaflet map or type the coordinates.
 */
export function ZoneForm({ value, onChange, errors }: FormProps<ZoneDef>) {
  const b = useContentBundle();
  const z: ZoneDef = { kind: 'place', shape: 'polar', staleMinutes: 0, effects: [], ...value };
  const defaultRadiusM =
    withDefaults(zoneDefaultsDefaults(), b.effectRules?.zoneDefaults ?? {}).radiusM ?? 150;
  const others = (b.zones ?? []).filter((x): x is ZoneDef => x != null && x.id !== z.id);
  const polar = z.polar ?? { lat: 0, lng: 0, radiusM: 0 };
  const grid = z.grid ?? { minLat: 0, minLng: 0, maxLat: 0, maxLng: 0 };
  const shapeIssues = [...(errors.polar ?? []), ...(errors.grid ?? [])];
  const num = (label: string, v: number | undefined, write: (n: number) => void, step = 0.0001) => (
    <NumberField label={label} step={step} min={-180} value={v} onChange={(n) => write(n ?? 0)} />
  );
  return (
    <>
      <Fieldset legend="Zone">
        <TextField
          label="Id"
          mono
          value={z.id}
          issues={errors.id}
          onChange={(v) => onChange({ ...z, id: v })}
        />
        <TextField label="Name" value={z.name} onChange={(v) => onChange({ ...z, name: v })} />
        <TextField
          label="Kind"
          mono
          hint="place (or any label); home and camp (outposts) are the base zones' own."
          value={z.kind}
          issues={errors.kind}
          onChange={(v) => onChange({ ...z, kind: v })}
        />
        <NumberField
          label="Stale minutes"
          hint="0 = effectRules.zoneDefaults.staleMinutes."
          integer
          min={0}
          value={z.staleMinutes}
          issues={errors.staleMinutes}
          onChange={(v) => onChange({ ...z, staleMinutes: v ?? 0 })}
        />
        <ZoneEffectPicker
          label="Effects while inside"
          value={z.effects}
          issues={errors.effects}
          onChange={(effects) => onChange({ ...z, effects })}
        />
      </Fieldset>
      <Fieldset legend="Shape">
        <SelectField
          label="Shape"
          value={z.shape}
          options={[
            { value: 'polar', label: 'polar (a circle)' },
            { value: 'grid', label: 'grid (a lat/lng box)' },
          ]}
          onChange={(v) =>
            onChange(
              v === 'grid'
                ? {
                    ...z,
                    shape: 'grid',
                    polar: null,
                    grid: z.grid ?? boxAround(polar, defaultRadiusM),
                  }
                : {
                    ...z,
                    shape: 'polar',
                    grid: null,
                    polar: z.polar ?? centreOf(grid, defaultRadiusM),
                  },
            )
          }
        />
        {z.shape === 'grid' ? (
          <>
            {num('Min lat', grid.minLat, (n) => onChange({ ...z, grid: { ...grid, minLat: n } }))}
            {num('Min lng', grid.minLng, (n) => onChange({ ...z, grid: { ...grid, minLng: n } }))}
            {num('Max lat', grid.maxLat, (n) => onChange({ ...z, grid: { ...grid, maxLat: n } }))}
            {num('Max lng', grid.maxLng, (n) => onChange({ ...z, grid: { ...grid, maxLng: n } }))}
          </>
        ) : (
          <>
            {num('Latitude', polar.lat, (n) => onChange({ ...z, polar: { ...polar, lat: n } }))}
            {num('Longitude', polar.lng, (n) => onChange({ ...z, polar: { ...polar, lng: n } }))}
            <NumberField
              label="Radius m"
              hint={`0 = the default (${defaultRadiusM} m); otherwise 10..5000.`}
              min={0}
              max={5000}
              value={polar.radiusM}
              onChange={(n) => onChange({ ...z, polar: { ...polar, radiusM: n ?? 0 } })}
            />
          </>
        )}
        <div className="sm:col-span-2">
          <IssueList issues={shapeIssues} />
        </div>
        <ZoneMap zone={z} others={others} defaultRadiusM={defaultRadiusM} onChange={onChange} />
      </Fieldset>
    </>
  );
}

const DEG = 111_320;
function boxAround(p: { lat?: number; lng?: number; radiusM?: number }, defaultRadiusM: number) {
  const r = (p.radiusM || defaultRadiusM) / DEG;
  const lat = p.lat ?? 0;
  const lng = p.lng ?? 0;
  const rl = r / Math.max(0.01, Math.cos((lat * Math.PI) / 180));
  return { minLat: lat - r, minLng: lng - rl, maxLat: lat + r, maxLng: lng + rl };
}
function centreOf(
  g: { minLat?: number; minLng?: number; maxLat?: number; maxLng?: number },
  defaultRadiusM: number,
) {
  if (!g.minLat && !g.maxLat) return { lat: 0, lng: 0, radiusM: defaultRadiusM };
  return {
    lat: ((g.minLat ?? 0) + (g.maxLat ?? 0)) / 2,
    lng: ((g.minLng ?? 0) + (g.maxLng ?? 0)) / 2,
    radiusM: 0,
  };
}

/** A `health.workout` trigger's `catIn` (Outta-Ten): only workouts of these categories fire it; none ticked = any category. */
function CategoryChecks({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string[] | undefined;
  onChange: (next: string[]) => void;
}) {
  const list = Array.isArray(value) ? value : [];
  return (
    <div className="sm:col-span-2">
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      <div className="mt-1 flex flex-wrap gap-3">
        {ACTIVITY_CATEGORIES.map((c) => (
          <label key={c} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              aria-label={`${label} ${c}`}
              checked={list.includes(c)}
              onChange={(e) =>
                onChange(e.target.checked ? [...list, c] : list.filter((x) => x !== c))
              }
            />
            {c}
          </label>
        ))}
      </div>
      <p className="mt-0.5 text-xs text-bruise">
        Only these workout categories fire it (none = any).
      </p>
    </div>
  );
}
